import { Button, capitalize, Stack, styled } from "@mui/material";
import { customColors } from "models/customColors";
import React, { useRef, useState } from "react";
import { ReactComponent as SuccessIcon } from "images/success.svg";
import { toast } from "react-toastify";
import { Spinner } from "UI/Spinner";
import { toastifyConfig } from "./ReferralForm/ReferralForm.common";

const UPLOAD_FILE_SIZE_LIMIT_IN_BYTES = 100 * 1024 * 1024;

export const FileInput = styled("input")`
  height: 1px;
  width: 1px;

  overflow: hidden;
  opacity: 0;
  height: 0;
`;

export const InputHolder = styled("label")`
  align-self: center;
  margin: 0;
  height: 47px;
  min-width: 180px;
  max-width: 400px;
  cursor: pointer;
`;

const ActionButton = styled(Stack)`
  padding: 8px;
  border-radius: 8px;

  justify-content: center;
  align-items: center;

  color: ${customColors.white};
  background-color: ${customColors.mainColor};
  cursor: pointer;
  &:hover {
    background-color: #666;
  }
`;

const showSuccessNotification = () =>
  toast(
    () => (
      <Stack direction={"row"} gap={1}>
        <SuccessIcon />
        <Stack>
          Upload complete.
          <div className="text-sm">
            Thank you for uploading the file. It will be available for download
            shortly.
          </div>
        </Stack>
      </Stack>
    ),
    toastifyConfig
  );

export const FileList = ({
  files,
  referralId,
  originator,
  showError,
  refreshData,
}) => {
  const uploadFilesInput = useRef();
  const [loading, setLoading] = useState(false);

  const handleUpload = async (event) => {
    const newFile = uploadFilesInput?.current?.files?.[0];
    const size = newFile?.size;

    if (size > UPLOAD_FILE_SIZE_LIMIT_IN_BYTES) {
      showError("Your file size is larger than 100MB.");
      // @ts-ignore
      if (uploadFilesInput?.current?.value) {
        // @ts-ignore
        uploadFilesInput.current.value = "";
      }
      return;
    }

    const formData = new FormData();
    formData.append("dashboardAccessToken", originator.dashboardAccessToken);
    const fileContents = [newFile];
    fileContents.forEach((file) => {
      formData.append("files[]", file);
    });

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/referral/${referralId}/files/upload`,
        {
          method: "POST",
          body: formData,
        }
      );
      const result = await response.json();

      if (response?.status !== 200 || result?.error) {
        showError(
          capitalize(result?.message ?? result?.error ?? "Something went wrong")
        );
        return;
      }

      showSuccessNotification();
      refreshData();
    } catch (error) {
      showError(
        capitalize(error?.message ?? error?.error ?? "Something went wrong")
      );
    } finally {
      setLoading(false);
      uploadFilesInput.current.value = "";
    }
  };

  // /referral/{referralId}/files/upload

  const downloadFile = async (fileId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/referral/${referralId}/files/download/${fileId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            dashboardAccessToken: originator?.dashboardAccessToken ?? "",
          }),
        }
      );

      const fileName = files.find((file) => file.id === fileId)?.originalName;

      if (response?.status !== 200) {
        showError("Something went wrong");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      showError(error?.message ?? "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // sort files by originalName
  const sortedFiles = files.sort((a, b) =>
    a.originalName.localeCompare(b.originalName)
  );

  return (
    <div className="p-4 flex flex-col justify-between items-stretch max-h-96 h-full w-full">
      <div className="flex-1 mb-4 overflow-y-auto w-full">
        {sortedFiles?.map((file) => (
          <div
            key={file.id}
            className={[
              "p-2 bg-white text-left",
              "flex flex-col gap-x-4 gap-y-0 sm:flex-row justify-between items-start sm:items-center",
              "border-b"
            ].join(" ")}
          >
            <div className="flex-1 mb-2 sm:mb-0">
              <p className="text-gray-800 font-medium">{file.originalName}</p>
              <p className="text-gray-600 text-sm">
                Created at: {new Date(file.createdAt).toLocaleString()}
              </p>
            </div>
            <p className="text-gray-600 text-sm mb-2 sm:mb-0">
              Size: {(file.contentLength / 1024).toFixed(2)} KB
            </p>
            <Button onClick={() => downloadFile(file?.id)}>Download</Button>
          </div>
        ))}
      </div>
      <InputHolder htmlFor="image-input">
        <ActionButton>Upload File</ActionButton>
        <FileInput
          ref={uploadFilesInput}
          id="image-input"
          name="preview"
          onChange={handleUpload}
          type="file"
          multiple={false}
        />
      </InputHolder>
      {loading && <Spinner />}
    </div>
  );
};
